import React, { useState, useEffect, useContext } from 'react';
import { Container, Button, Card, Table } from 'react-bootstrap';
import { useNavigate, Link } from 'react-router-dom';
import { BsGrid, BsList, BsPencil } from 'react-icons/bs';
import { FaTv } from 'react-icons/fa';
import './ModdableCRTs.css'; // Your existing CSS for hover effects, etc.
import { ToastContainer, toast } from 'react-toastify';
import { AuthContext } from '../contexts/AuthContext';
import { Helmet } from 'react-helmet';
import CRTSubmissionModal from '../components/CRTSubmissionModal';

const STORE_API_URL = process.env.REACT_APP_API_URL;
const DOCS_URL = process.env.REACT_APP_DOCS_URL;

function ModdableCRTs() {
  const { isAuthenticated, validateToken, role, user, token } = useContext(AuthContext);
  
  // Add state for CRT submission modal
  const [showCrtModal, setShowCrtModal] = useState(false);
  
  // Which mod the user is filtering by (rgb, component, svideo, composite, or all)
  const [selectedMod, setSelectedMod] = useState('rgb');
  
  // Holds the fetched CRT data
  const [crtData, setCrtData] = useState([]);
  
  // View mode toggle (card/list)
  const [viewMode, setViewMode] = useState(localStorage.getItem('crtViewMode') || 'list');

  const [sortConfig, setSortConfig] = useState({ key: 'title', direction: 'asc' });

  const navigate = useNavigate();

  // Run fetch whenever selectedMod changes
  useEffect(() => {
    fetchCrtData();
  }, [selectedMod]);

  const fetchCrtData = async () => {
    try {
      // Build query string
      let queryParams = [];
      
      if (selectedMod && selectedMod !== 'all') {
        queryParams.push(`types=${selectedMod}`);
      }

      // Always fetch moddable CRTs
      queryParams.push('moddable=yes');

      const queryString = queryParams.length ? `?${queryParams.join('&')}` : '';

      const response = await fetch(`${STORE_API_URL}/crts/moddable${queryString}`);
      if (!response.ok) {
        throw new Error(`Failed to fetch CRTs: ${response.statusText}`);
      }

      const data = await response.json();
      setCrtData(data || []);
    } catch (error) {
      console.error('Error fetching CRT data:', error);
    }
  };

  // Map each mod type to the relevant link field (for card clicks)
  const modLinkFieldMap = {
    rgb: 'rgb_mod_link',
    component: 'component_mod_link',
    svideo: 'svideo_mod_link',
    composite: 'composite_mod_link'
  };

  // Decide which mod type to filter by when user clicks a button
  const handleModTypeClick = (modType) => {
    setSelectedMod(modType);
  };

  // Sort function for table data
  const sortData = (data, sortKey, direction) => {
    const normalize = (str) => (str || '').toString().trim().toLowerCase();
    
    return [...data].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortKey) {
        case 'title':
          aValue = normalize(a.title);
          bValue = normalize(b.title);
          break;
        case 'chroma':
          aValue = normalize(Array.isArray(a.chroma) ? a.chroma.join(', ') : a.chroma);
          bValue = normalize(Array.isArray(b.chroma) ? b.chroma.join(', ') : b.chroma);
          break;
        case 'chassis':
          aValue = normalize(Array.isArray(a.chassis) ? a.chassis.join(', ') : a.chassis);
          bValue = normalize(Array.isArray(b.chassis) ? b.chassis.join(', ') : b.chassis);
          break;
        default:
          aValue = normalize(a[sortKey]);
          bValue = normalize(b[sortKey]);
      }
      
      if (direction === 'asc') {
        return aValue.localeCompare(bValue);
      } else {
        return bValue.localeCompare(aValue);
      }
    });
  };

  // Handle column header click
  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  // Get sort direction indicator
  const getSortIndicator = (columnKey) => {
    if (sortConfig.key !== columnKey) return '';
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  // Group CRTs by brand so we can display them in sections
  const groupedByBrand = crtData.reduce((acc, crt) => {
    const brand = crt.brand || 'Unknown Brand';
    if (!acc[brand]) {
      acc[brand] = [];
    }
    acc[brand].push(crt);
    return acc;
  }, {});

  // Sort CRTs within each brand group
  Object.keys(groupedByBrand).forEach(brand => {
    groupedByBrand[brand] = sortData(groupedByBrand[brand], sortConfig.key, sortConfig.direction);
  });

  // Create small labels for each mod that is "Yes"
  // e.g., [RGB] [Component] [S-Video] ...
  const getModLabels = (crt) => {
    return [
      { type: 'RGB', link: crt.rgb_mod_link, show: crt.rgb_modifiable === 'Yes' },
      { type: 'Component', show: crt.component_modifiable === 'Yes' },
      { type: 'S-Video', show: crt.svideo_modifiable === 'Yes' },
      { type: 'Composite', show: crt.composite_modifiable === 'Yes' }
    ].filter(label => label.show);
  };

  // Update localStorage when view mode changes
  const handleViewModeChange = (mode) => {
    setViewMode(mode);
    localStorage.setItem('crtViewMode', mode);
  };

  return (
    <>
      <Helmet>
        <title>RGB, Component, S-Video, and Composite Moddable CRTs | Sunthar's Super Sector</title>
        <meta name="description" content="This page summarizes all the CRTs that you can modify for RGB, Component, S-Video, and Composite" />
      </Helmet>
      <Container>
        <ToastContainer />
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h2 className="mb-0">Moddable CRTs</h2>
          <div >
          <Button 
              variant="primary" 
              onClick={() => setShowCrtModal(true)}
              className="me-2 btn-sm"
              style={{
                backgroundColor: '#fca311',
                color: '#000',
                border: 'none',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }}
            >
              <FaTv style={{ verticalAlign: 'middle', marginRight: '8px', marginBottom: '2px' }} /> CRT not listed?
            </Button>
            </div>
          <div>
            {/* CRT not listed button */}


            {/* View toggle */}
            <Button
              variant="outline-secondary"
              className="me-2 btn-sm"
              onClick={() => handleViewModeChange(viewMode === 'card' ? 'list' : 'card')}
            >
              {viewMode === 'card' ? <BsList /> : <BsGrid />}
            </Button>

            {/* Link to not moddable CRTs */}
            <Link 
              to="/not-moddable"
              className="btn btn-outline-secondary btn-sm me-2"
            >
              Not RGB Moddable CRTs
            </Link> 
            
            {/* Link to classic moddable CRTs guide */}
            <a 
              href={`${DOCS_URL}/guides/crt-rgb-mod/moddable.html`}
              rel="noopener noreferrer"
              className="btn btn-outline-info btn-sm"
            >
              Classic List
            </a>
          </div>
        </div>

        {/* CRT Submission Modal */}
        <CRTSubmissionModal 
          show={showCrtModal}
          onHide={() => setShowCrtModal(false)}
          submissionType="help"
        />

        <div className="row">
          <div className="col-lg-9 col-12">
            {/* Button group: Mod Type */}
            <div className="mb-2">
              <Button
                size="sm"
                variant={selectedMod === 'rgb' ? 'primary' : 'outline-primary'}
                onClick={() => handleModTypeClick('rgb')}
                className="me-2"
              >
                RGB
              </Button>
              <Button
                size="sm"
                variant={selectedMod === 'component' ? 'primary' : 'outline-primary'}
                onClick={() => handleModTypeClick('component')}
                className="me-2"
              >
                Component
              </Button>
              <Button
                size="sm"
                variant={selectedMod === 'svideo' ? 'primary' : 'outline-primary'}
                onClick={() => handleModTypeClick('svideo')}
                className="me-2"
              >
                S-Video
              </Button>
              <Button
                size="sm"
                variant={selectedMod === 'composite' ? 'primary' : 'outline-primary'}
                onClick={() => handleModTypeClick('composite')}
              >
                Composite
              </Button>
            </div>

            {/* Display total count */}
            <div className="mb-3">
              <small className="text-muted">
                Total: {crtData.length} CRT{crtData.length !== 1 ? 's' : ''} found
              </small>
            </div>

            {viewMode === 'list' ? (
              // List View
              <div className="crt-table">
                {Object.keys(groupedByBrand).map((brand) => (
                  <div key={brand} id={`brand-${brand.replace(/\s+/g, '-')}`}>
                    <h4 style={{ marginBottom: '1rem' }}>{brand}</h4>
                    <Table striped hover className="mb-4">
                      <thead>
                        <tr>
                          <th onClick={() => handleSort('title')} style={{ cursor: 'pointer' }}>
                            Model{getSortIndicator('title')}
                          </th>
                          <th 
                            onClick={() => handleSort('chroma')} 
                            className="d-none d-md-table-cell"
                            style={{ cursor: 'pointer' }}
                          >
                            Jungle IC{getSortIndicator('chroma')}
                          </th>
                          <th onClick={() => handleSort('chassis')} style={{ cursor: 'pointer' }}>
                            Chassis{getSortIndicator('chassis')}
                          </th>
                          <th className="d-none d-md-table-cell">Mod Types</th>
                          {isAuthenticated && role === 'super' && <th>Actions</th>}
                        </tr>
                      </thead>
                      <tbody>
                        {groupedByBrand[brand].map((crt, index) => {
                          const labels = getModLabels(crt);
                          return (
                            <tr key={index}>
                              <td>
                                <div className="d-flex align-items-center">
                                  {crt.slug ? (
                                    <Link 
                                      to={`/crts/${crt.slug}`}
                                      className="text-decoration-none"
                                    >
                                      {crt.title}
                                    </Link>
                                  ) : (
                                    crt.title
                                  )}
                                  {crt.rgb_mux_calculator_enabled && (
                                    <a 
                                      href={`/rgb-mux-calculator?crt_slug=${crt.slug}`}
                                      className="ms-2 text-decoration-none d-flex align-items-center"
                                      onClick={(e) => e.stopPropagation()}
                                    >
                                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator" viewBox="0 0 16 16">
                                        <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                                        <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
                                      </svg>
                                    </a>
                                  )}
                                </div>
                              </td>
                              <td className="d-none d-md-table-cell">
                                {Array.isArray(crt.chroma) ? crt.chroma.join(', ') : crt.chroma}
                              </td>
                              <td>
                                {Array.isArray(crt.chassis) ? crt.chassis.join(', ') : crt.chassis}
                              </td>
                              <td className="d-none d-md-table-cell">
                                {labels.map((label) => (
                                  <span key={label.type} className="badge bg-success me-1">
                                    {label.type}
                                  </span>
                                ))}
                              </td>
                              {isAuthenticated && role === 'super' && (
                                <td>
                                  <Button
                                    variant="outline-primary"
                                    size="sm"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      navigate(`/superadmin/edit-crt/${crt._id}`);
                                    }}
                                  >
                                    Edit
                                  </Button>
                                </td>
                              )}
                            </tr>
                          );
                        })}
                      </tbody>
                    </Table>
                  </div>
                ))}
              </div>
            ) : (
              // Card View (existing code)
              <div>
                {Object.keys(groupedByBrand).map((brand) => (
                  <div key={brand} id={`brand-${brand.replace(/\s+/g, '-')}`}>
                    <h4 style={{ marginBottom: '1rem' }}>{brand}</h4>
                    <div className="row">
                      {groupedByBrand[brand].map((crt, index) => {
                        // If the user has selected a certain mod type AND there's a link for it
                        const linkField = modLinkFieldMap[selectedMod];
                        const hasLink = linkField && crt[linkField];
                        const hasSlug = crt.slug;
                        // Highlight the card if it has a link
                        const cardClass = `card-hover ${hasLink ? 'has-link' : 'no-link'}`;

                        // Collect the mod labels for this CRT
                        const labels = getModLabels(crt);

                        return (
                          <div key={index} className="col-xl-3 col-lg-4 col-md-6 col-sm-12 mb-3">
                            <Card
                              className={cardClass}
                              style={{ height: '100%', cursor: 'pointer' }}
                              onClick={() => {
                                if (hasSlug) {
                                  navigate(`/crts/${crt.slug}`);
                                } else {
                                  toast.info('No detail page available for this CRT');
                                }
                              }}
                            >
                              <Card.Body className="d-flex flex-column">
                                <Card.Title>
                                  <div className="d-flex align-items-center">
                                    {crt.title}
                                  </div>
                                </Card.Title>
                                <Card.Text style={{ minHeight: '24px' }}>
                                  {crt.chassis && Array.isArray(crt.chassis) && crt.chassis.length > 0 && 
                                    <><strong>Chassis:</strong> {crt.chassis.join(", ")}</>}
                                </Card.Text>
                                {/* Show small labels for each mod that is "Yes" */}
                                <Card.Text className="mt-auto">
                                  <div className="d-flex align-items-center">
                                    {labels.map((label) => (
                                      label.link ? (
                                        <a
                                          key={label.type}
                                          href={label.link}
                                          rel="noreferrer"
                                          className="badge bg-success me-1"
                                          style={{ textDecoration: 'none' }}
                                          onClick={(e) => e.stopPropagation()}
                                        >
                                          {label.type}
                                        </a>
                                      ) : (
                                        <span key={label.type} className="badge bg-success me-1">
                                          {label.type}
                                        </span>
                                      )
                                    ))}
                                    {crt.rgb_mux_calculator_enabled && (
                                      <a 
                                        href={`/rgb-mux-calculator?crt_slug=${crt.slug}`}
                                        className="text-decoration-none d-flex align-items-center"
                                        onClick={(e) => e.stopPropagation()}
                                      >
                                        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-calculator" viewBox="0 0 16 16">
                                          <path d="M12 1a1 1 0 0 1 1 1v12a1 1 0 0 1-1 1H4a1 1 0 0 1-1-1V2a1 1 0 0 1 1-1h8zM4 0a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V2a2 2 0 0 0-2-2H4z"/>
                                          <path d="M4 2.5a.5.5 0 0 1 .5-.5h7a.5.5 0 0 1 .5.5v2a.5.5 0 0 1-.5.5h-7a.5.5 0 0 1-.5-.5v-2zm0 4a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm3-6a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v1a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-1zm0 3a.5.5 0 0 1 .5-.5h1a.5.5 0 0 1 .5.5v4a.5.5 0 0 1-.5.5h-1a.5.5 0 0 1-.5-.5v-4z"/>
                                        </svg>
                                      </a>
                                    )}
                                  </div>
                                </Card.Text>
                              </Card.Body>
                            </Card>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
          {/* Brand sidebar for desktop only */}
          <div className="col-lg-3 d-none d-lg-block">
            <div className="brand-toc-sidebar">
              <h6 className="brand-toc-title mb-3">Brands</h6>
              <ul className="brand-toc-list list-unstyled">
                {Object.keys(groupedByBrand).map((brand) => (
                  <li key={brand}>
                    <a
                      href={`#brand-${brand.replace(/\s+/g, '-')}`}
                      className="brand-toc-link"
                    >
                      {brand}
                    </a>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </Container>
    </>
  );
}

export default ModdableCRTs;
